import { ROOM_TYPES } from './constants'
import type { StyleOption } from './types'

// Readable prompt for formatting room types
const formatRoomType = (roomType: string): string => roomType.replace(/-/g, ' ')

// Restyle Constants
// Negative tokens for Restyler Injection
export const ROOM_NEGATIVE_TOKENS: Record<string, string[]> = {
  [formatRoomType('living-room')]: [
    'bed',
    'bathtub',
    'toilet',
    'shower',
    'stove',
    'oven',
    'sink',
    'refrigerator',
    'urinal',
    'bidet',
  ],
  [formatRoomType('bedroom')]: [
    'sofa',
    'toilet',
    'bathtub',
    'shower',
    'stove',
    'oven',
    'refrigerator',
    'dining table',
    'coffee table',
    'tv stand',
  ],
  [formatRoomType('dining-room')]: [
    'bed',
    'toilet',
    'bathtub',
    'shower',
    'desk',
    'nightstand',
    'computer desk',
    'couch',
    'wardrobe',
    'vanity',
  ],
  [formatRoomType('kitchen')]: [
    'bed',
    'sofa',
    'couch',
    'toilet',
    'bathtub',
    'shower',
    'coffee table',
    'tv stand',
    'dresser',
    'armchair',
  ],
  [formatRoomType('bathroom')]: [
    'bed',
    'sofa',
    'dining table',
    'desk',
    'coffee table',
    'tv stand',
    'bookshelf',
    'armchair',
    'dining chair',
    'sectional',
  ],
  [formatRoomType('foyer')]: [
    'bed',
    'dining table',
    'bathtub',
    'shower',
    'toilet',
    'stove',
    'oven',
    'desk',
    'sofa',
    'refrigerator',
  ],
  [formatRoomType('entryway')]: [
    'bed',
    'dining table',
    'bathtub',
    'shower',
    'toilet',
    'stove',
    'oven',
    'desk',
    'sofa',
    'refrigerator',
  ],
  [formatRoomType('office')]: [
    'bed',
    'dining table',
    'bathtub',
    'shower',
    'toilet',
    'stove',
    'oven',
    'sofa',
    'refrigerator',
    'coffee table',
  ],
  [formatRoomType('patio')]: [
    'bed',
    'toilet',
    'shower',
    'bathtub',
    'kitchen counter',
    'oven',
    'stove',
    'desk',
    'refrigerator',
    'wardrobe',
  ],
  [formatRoomType('home-theater')]: [
    'bed',
    'toilet',
    'shower',
    'bathtub',
    'dining table',
    'stove',
    'oven',
    'refrigerator',
    'sink',
    'washing machine',
  ],
  [formatRoomType('playroom')]: [
    'toilet',
    'shower',
    'bathtub',
    'stove',
    'oven',
    'refrigerator',
    'dining table',
    'sectional',
    'king bed',
    'washing machine',
  ],
  [formatRoomType('laundry-room')]: [
    'bed',
    'sofa',
    'coffee table',
    'dining table',
    'tv stand',
    'bathtub',
    'toilet',
    'shower',
  ],
  [formatRoomType('sunroom')]: [
    'bed',
    'toilet',
    'shower',
    'bathtub',
    'refrigerator',
    'stove',
    'oven',
    'washing machine',
    'dryer',
    'computer desk',
  ],
  [formatRoomType('home-gym')]: [
    'bed',
    'dining table',
    'coffee table',
    'toilet',
    'shower',
    'bathtub',
    'stove',
    'oven',
    'refrigerator',
    'sofa',
  ],
  [formatRoomType('hallway')]: [
    'bed',
    'sofa',
    'dining table',
    'toilet',
    'shower',
    'bathtub',
    'stove',
    'oven',
    'refrigerator',
    'desk',
  ],
  [formatRoomType('library')]: [
    'bed',
    'toilet',
    'shower',
    'bathtub',
    'stove',
    'oven',
    'refrigerator',
    'treadmill',
    'exercise equipment',
    'elliptical',
  ],
  [formatRoomType('walk-in-closet')]: [
    'dining table',
    'coffee table',
    'toilet',
    'shower',
    'bathtub',
    'stove',
    'oven',
    'refrigerator',
    'tv stand',
  ],
}

export const validateRoomNegativeTokens = (): void => {
  ROOM_TYPES.forEach((roomType: string) => {
    const formattedType = formatRoomType(roomType)
    if (
      !Object.prototype.hasOwnProperty.call(ROOM_NEGATIVE_TOKENS, formattedType)
    ) {
      console.warn(
        `Warning: No negative tokens defined for room type: ${roomType}`
      )
    }
  })
}

/**
 * Example usage:
 *
 * // Get a style from STYLE_OPTIONS
 * const selectedStyle = STYLE_OPTIONS.find(style => style.value === 'wabi-sabi');
 *
 * // Get the room type from room analysis (saved in DB as 'living-room')
 * const roomType = 'living-room';
 *
 * // Combine them to create a room-specific prompt
 * const customPrompt = injectRoomType(selectedStyle.prompt, roomType);
 *
 * // Result: "Photorealistic living room, Wabi Sabi interior, minimalistic and tranquil..."
 */

// Style options for restyle
// DO NOT REMOVE photorealistic token from the begininng of the prompt! this is used in restyle.ts
const STYLE_OPTIONS: StyleOption[] = [
  {
    value: 'wabi-sabi',
    label: 'Wabi Sabi',
    hot: false,
    image: '/assets/restyle/styles/wabi-sabi.jpg',
    prompt:
      'Photorealistic Wabi Sabi interior, minimalistic and tranquil, organic textures, neutral earthy palette, asymmetrical balance, natural wood elements, imperfect and weathered surfaces, open space with light and shadow interplay, soft filtered sunlight, raw linen fabrics, rustic stone and clay, serene and introspective atmosphere, elegant imperfection, traditional Japanese aesthetics, harmony in design',
  },
  {
    value: 'yellowstone',
    label: 'Yellowstone',
    hot: false,
    image: '/assets/restyle/styles/yellowstone.jpg',
    prompt:
      'Photorealistic Yellowstone style, featuring expansive vaulted ceilings with exposed timber beams, rugged stone walls. Earth-toned color palette with deep leathers, textured wool, and raw wood finishes. Woven textiles, panoramic mountain views. Balanced elegance and rusticity with ranch-inspired artwork. Warm ambient lighting, natural materials, inviting and grounded atmosphere.',
  },
  {
    value: 'bauhaus',
    label: 'Bauhaus',
    hot: false,
    image: '/assets/restyle/styles/bauhaus.jpg',
    prompt:
      'Photorealistic Bauhaus style, minimalist design, clean lines, geometric shapes, industrial aesthetic, neutral tones, modern design, sleek metal accents, simple yet elegant, abstract art, bold color accents, space-efficient, contemporary, balance of form and function, modernist architecture',
  },
  {
    value: 'cabin fever',
    label: 'Cabin Fever',
    hot: false,
    image: '/assets/restyle/styles/cabin-fever.jpg',
    prompt:
      'Photorealistic Cabin fever style, rustic French country, wooden beams, woven textures, earthy tones, vintage elements, cozy ambiance, warm lighting, woolen textiles, warm and inviting, timeless, natural textures, traditional craftsmanship',
  },
  {
    value: 'bohemian',
    label: 'Bohemian',
    hot: true,
    image: '/assets/restyle/styles/boheme.jpg',
    prompt:
      'Photorealistic Bohemian style, eclectic design, natural materials, relaxed and lived-in feel, organic textures, layered textiles, earthy tones, vintage accents, cozy and inviting, artisanal craftsmanship, comfortable, boho-chic, rustic warmth, handmade elements, relaxed elegance',
  },
  {
    value: 'california',
    label: 'California',
    hot: true,
    image: '/assets/restyle/styles/california.jpg',
    prompt:
      'Photorealistic high-end California style, modern luxury, clean lines, minimalistic design, sophisticated elegance, open space, neutral tones, polished finishes, natural light, contemporary design, statement lighting, refined textures, airy and spacious, premium materials, calm and serene ambiance, modern art, subtle opulence',
  },
  {
    value: 'colonial',
    label: 'Colonial',
    hot: false,
    image: '/assets/restyle/styles/colonial.jpg',
    prompt:
      'Photorealistic Colonial style, traditional craftsmanship, elegant symmetry, rich wood paneling, classic design, refined molding, neutral tones, vintage details, warm lighting, timeless appeal, comfortable yet formal, natural textures, balanced layout, historical charm, sophisticated warmth',
  },
  {
    value: 'versailles',
    label: 'Versailles',
    hot: false,
    image: '/assets/restyle/styles/versailles.jpg',
    prompt:
      'Photorealistic King Louis style, opulent, regal elegance, intricate woodwork, luxurious fabrics, gilded accents, classical design, ornate fixtures, rich textures, symmetrical design, elaborate detailing, polished hardwood floors, soft lighting, aristocratic charm, refined sophistication, old-world luxury',
  },
  {
    value: 'copenhagen',
    label: 'Copenhagen',
    hot: true,
    image: '/assets/restyle/styles/copenhagen.jpg',
    prompt:
      'Photorealistic Copenhagen style, minimalist design, soft neutral palette, sleek lines, functional design, Scandinavian influences, modern simplicity, natural materials, airy and spacious, light wood accents, clean aesthetics, light-filled spaces, refined and understated, organic textures, serene and inviting, contemporary elegance',
  },
  {
    value: 'demure',
    label: 'Demure',
    hot: true,
    image: '/assets/restyle/styles/demure.jpg',
    prompt:
      'Photorealistic Demure style, understated elegance, subtle sophistication, soft neutral tones, refined textures, delicate lighting, muted color palette, classic design elements, minimalist with warmth, timeless design, soft furnishings, balanced layout, calming ambiance, serene atmosphere, high-end finishes, harmonious details',
  },
  {
    value: 'hamptons',
    label: 'Hamptons',
    hot: false,
    image: '/assets/restyle/styles/hamptons.jpg',
    prompt:
      'Photorealistic Hamptons style interior, bright natural light, quiet luxury color palette, soft neutrals, white walls, linen and cotton fabrics, rattan and jute textures, reclaimed wood accents, elegant informality, indoor-outdoor flow, understated luxury, symmetrical design, timeless charm, artisanal craftsmanship, relaxed sophistication',
  },
  {
    value: 'miami',
    label: 'Miami',
    hot: false,
    image: '/assets/restyle/styles/miami.jpg',
    prompt:
      'Photorealistic Miami style, luxury, modern glamour, bold accents, clean lines, high-end materials, chic sophistication, neutral base with vibrant pops, glossy finishes, statement lighting, mirrored surfaces, polished marble, contemporary design, open spaces, tropical influences, soft, warm lighting, elegant and lively ambiance',
  },
  {
    value: 'provence',
    label: 'Provence',
    hot: true,
    image: '/assets/restyle/styles/provence.jpg',
    prompt:
      'Photorealistic Provence style, rustic charm, soft pastel palette, natural materials, warm, sun-drenched ambiance, French countryside, vintage elements, light wood accents, wicker and rattan, botanical accents, soft linens, relaxed elegance, earthy tones, serene and inviting, simple yet sophisticated, timeless French aesthetic',
  },
  {
    value: 'rio',
    label: 'Rio',
    hot: false,
    image: '/assets/restyle/styles/rio.jpg',
    prompt:
      'Photorealistic Rio style, vibrant tropical influences, bold colors, eclectic decor, natural textures, woven materials, coastal charm, artisanal craftsmanship, relaxed and playful, natural wood accents, lively patterns, warm and inviting atmosphere, earthy and bright, exotic feel',
  },
  {
    value: 'paris',
    label: 'Paris',
    hot: true,
    image: '/assets/restyle/styles/paris.jpg',
    prompt:
      'Photorealistic Parisian style, refined elegance, timeless beauty, classic architecture, high ceilings, ornate moldings, chic simplicity, luxurious yet understated, neutral palette with subtle accents, sophisticated design, artful decor, vintage touches, natural light, parquet flooring, gallery-style artwork, contemporary meets classical, curated sophistication, relaxed luxury',
  },
  {
    value: 'minimal',
    label: 'Minimal',
    hot: false,
    image: '/assets/restyle/styles/minimal.jpg',
    prompt:
      'Photorealistic Minimal style, sleek design, clean lines, open spaces, neutral tones, functional design, understated elegance, simple yet refined, high-quality materials, sparse decor, focus on form and function, natural light, geometric shapes, smooth surfaces, modern finishes, uncluttered space, subtle textures, modernist aesthetic',
  },
  {
    value: 'storybook',
    label: 'Storybook',
    hot: false,
    image: '/assets/restyle/styles/storybook.jpg',
    prompt:
      'Photorealistic Shabby Chic style, vintage charm, soft pastel tones, distressed elements, feminine decor, floral patterns, elegant yet worn, vintage finds, rustic elegance, cozy and inviting, delicate fabrics, layered textures, romantic ambiance, weathered wood, soft lighting, rustic charm, elegant imperfection',
  },
  {
    value: 'transitional',
    label: 'Transitional',
    hot: false,
    image: '/assets/restyle/styles/transitional.jpg',
    prompt:
      'Photorealistic Transitional style, soft neutral palette, balanced blend of traditional and contemporary, clean architectural lines, elegant moldings, tailored textures, minimal ornamentation, natural light, mixed textures, refined materials, abstract art, understated luxury, timeless design, inviting and harmonious ambiance',
  },
  {
    value: 'havana',
    label: 'Havana',
    hot: false,
    image: '/assets/restyle/styles/havana.jpg',
    prompt:
      'Photorealistic Havana Cuba style, vintage charm, tiles on floor, plaster trim on walls, colonial architecture, bold patterns, ornate details, floral prints, rich textures, light-filled spaces, juxtaposition of old and new, artisanal craftsmanship, distressed finishes, lively atmosphere, layered fabrics, luxurious yet relaxed elegance',
  },
  {
    value: 'persia',
    label: 'Persia',
    hot: false,
    image: '/assets/restyle/styles/persia.jpg',
    prompt:
      'Photorealistic Persian style interior, opulent design, intricate patterns, rich textiles, deep red and gold, ornate decorative elements, arched alcoves, classical symmetry, elegant stone, warm ambient lighting, detailed moldings, traditional motifs, cultural refinement, old-world charm, layered textures, timeless craftsmanship, regal and inviting atmosphere',
  },
  {
    value: 'barbie',
    label: 'Barbie Coquette',
    hot: false,
    image: '/assets/restyle/styles/barbie.jpg',
    prompt:
      'Photorealistic Barbie Coquette interior, soft pink palette, romantic French Rococo influence, blush-toned textures, ornate gold-framed mirrors, Parisian apartment charm, soft ambient lighting, baroque crown molding, pale wood floors, dreamy and ultra-feminine atmosphere, coquette elegance, graceful curves, pastel florals, antique detailing, hyper-stylized femininity',
  },
  {
    value: 'spanish-villa',
    label: 'Spanish Villa',
    hot: false,
    image: '/assets/restyle/styles/spanish-villa.jpg',
    prompt:
      'Photorealistic Spanish Villa style, rustic elegance, Mediterranean charm, terracotta tiles, intricate wooden beams, arched doorways, bold patterned tiles, natural wood accents, wrought iron details, earthy tones, warm and inviting atmosphere, textured walls, open and airy spaces, ornate fixtures, relaxed yet sophisticated vibe',
  },
  {
    value: 'scifi-movie',
    label: 'Scifi Movie',
    hot: false,
    image: '/assets/restyle/styles/scifi-movie.jpg',
    prompt:
      'Photorealistic Star Trek style, futuristic, sleek design, high-tech elements, glowing interfaces, metallic finishes, geometric shapes, neon lighting, minimalist space, space exploration aesthetic, sci-fi architecture, polished surfaces, dynamic lighting, bold colors, advanced technology, streamlined and functional, modernist sci-fi design',
  },
  {
    value: 'neo-tudor',
    label: 'Neo Tudor',
    hot: false,
    image: '/assets/restyle/styles/neo-tudor.jpg',
    prompt:
      'Photorealistic Neo-Tudor style, refined architecture, warm neutral palette, vintage-inspired decor, scenic outdoor view, blend of old-world charm and modern sophistication, cozy yet grand, artisanal details, stained glass',
  },
  {
    value: 'scandinavian',
    label: 'Scandinavian',
    hot: false,
    image: '/assets/restyle/styles/scandinavian.jpg',
    prompt:
      'Photorealistic Scandinavian style, minimalist, neutral tones, natural materials, light wood, soft lighting, cozy, airy, clean lines, modern, functional, calm, Nordic design, hygge, muted colors, textured surfaces',
  },
  {
    value: 'modern',
    label: 'Modern',
    hot: false,
    image: '/assets/restyle/styles/modern.jpg',
    prompt:
      'Photorealistic Modern style, minimalist, neutral tones, clean lines, sleek surfaces, metal accents, glass elements, steel details, contemporary, polished finishes, functional, elegant, high contrast, refined, sophisticated, open feel',
  },
  {
    value: 'industrial',
    label: 'Industrial',
    hot: false,
    image: '/assets/restyle/styles/industrial.jpg',
    prompt:
      'Photorealistic Industrial style, raw aesthetic, exposed brick, concrete floors, steel beams, reclaimed wood, metal fixtures, spacious layout, high ceilings, open areas, warehouse feel, minimalist design, artistic touches, raw materials',
  },
  {
    value: 'minimalist',
    label: 'Minimalist',
    hot: false,
    image: '/assets/restyle/styles/minimalist.jpg',
    prompt:
      'Photorealistic Minimalist style, extreme simplicity, functionality, neutral palette, crisp white, soft greys, subtle tones, serene atmosphere, uncluttered spaces, clean lines, open floor plans, light and shadow, peaceful, calm',
  },
  {
    value: 'mid-century modern',
    label: 'Mid-Century Modern',
    hot: false,
    image: '/assets/restyle/styles/mid-century-modern.jpg',
    prompt:
      'Photorealistic Mid-Century Modern style, 1950s-1960s design, natural wood, teak, walnut, organic shapes, warm tones, leather textures, functional, elegant, vibrant colors, mustard, teal, olive, retro vibe, clean lines, geometric patterns, timeless',
  },
  {
    value: 'traditional',
    label: 'Traditional',
    hot: false,
    image: '/assets/restyle/styles/traditional.jpg',
    prompt:
      'Photorealistic Traditional style, classic architectural details, plush textures, rich colors, deep reds, golds, greens, ornate accessories, crown molding, symmetrical layouts, luxurious fabrics, velvet, silk, sophisticated, timeless, elegant lighting',
  },
  {
    value: 'art deco',
    label: 'Art Deco',
    hot: false,
    image: '/assets/restyle/styles/art-deco.jpg',
    prompt:
      'Photorealistic Art Deco interior design, elegant symmetry, rich jewel tones like emerald green and gold, velvet textures, geometric wall patterns, brass and chrome accents, arched doorways, dramatic lighting, lacquered wood, luxurious materials like marble and terrazzo, fluted details, pink and teal color pops, editorial lighting, interior photography, wide-angle composition',
  },
  {
    value: 'coastal',
    label: 'Coastal',
    hot: false,
    image: '/assets/restyle/styles/coastal.jpg',
    prompt:
      'Photorealistic Coastal style, light and breezy, soft white and blue, ocean-inspired, natural textures, rattan, weathered wood, nautical motifs, maritime charm, comfortable linen, serene, airy, relaxed, sun-kissed',
  },
  {
    value: 'rustic',
    label: 'Rustic',
    hot: false,
    image: '/assets/restyle/styles/rustic.jpg',
    prompt:
      'Photorealistic Rustic style, natural elements, raw wood beams, earthy tones, rugged appeal, warm, cozy, lived-in, welcoming, earthy textures, simplicity, countryside aesthetic, functional, nature-inspired',
  },
  {
    value: 'cottage core',
    label: 'Cottage Core',
    hot: true,
    image: '/assets/restyle/styles/cottage-core.jpg',
    prompt:
      'Photorealistic Farmhouse style, rustic charm, modern comfort, warm wood beams, distressed elements, cozy textiles, welcoming, lived-in feel, neutral palette, earthy tones, timeless decor, open spaces, functional layouts, farm-inspired, homely elegance',
  },
  {
    value: 'japanese',
    label: 'Japanese',
    hot: false,
    image: '/assets/restyle/styles/japanese.jpg',
    prompt:
      'Photorealistic Japanese interior, serene and minimalist aesthetic, natural wood tones, low-profile design, soft ambient daylight, Zen-inspired decor, organic textures, handcrafted wooden accents, clean lines, spacious layout, calming neutral palette, wabi-sabi influence, harmonious and meditative ambiance, tranquil and balanced composition',
  },
  {
    value: 'mediterranean',
    label: 'Mediterranean',
    hot: false,
    image: '/assets/restyle/styles/mediterranean.jpg',
    prompt:
      'Photorealistic Mediterranean style, interior room design, whitewashed walls, warm neutral backdrop, natural materials, terracotta accents, rich textured fabrics, earthy color palette, warm wood tones, olive green touches, woven textiles, ceramic elements, arched details, coastal inspired',
  },
  {
    value: 'derelicte',
    label: 'Derelicte',
    hot: false,
    image: '/assets/restyle/styles/derelicte.jpg',
    prompt:
      'Photorealistic Derelicte style, high-fashion trash chic, avant-garde, deconstructed, distressed elements, recycled materials, raw surfaces, grunge aesthetics, artistic expression, intentionally unfinished, bold, unconventional, boundary-pushing',
  },
]

export default STYLE_OPTIONS
